import { useEffect } from 'react';

/**
 * QA 8384 — every modal in this app is a hand-rolled fixed overlay (there is no
 * Bootstrap JS driving them), so nothing ever locked the page behind one: the
 * content underneath scrolled away while the dialog sat still.
 *
 * Rather than thread an "is open" flag through all ~24 dialogs in 16 files —
 * where the next modal added would silently miss the fix — this watches the DOM
 * for the marker every one of them already carries, `aria-modal="true"`, and
 * freezes the page while at least one is mounted. Inline popovers use
 * role="dialog" WITHOUT aria-modal and are correctly left alone — except the
 * date picker on a phone, which is a full-screen backdrop and sets aria-modal
 * there so the page behind it is frozen too (QA 8648).
 *
 * Two scrollers, not one. The connector/public shells scroll the document, but
 * `body.admin-page` is `overflow: hidden` by design and the real scroller is
 * `.admin-content` inside the flex shell — so locking only the body would leave
 * every admin page (the referral tracking popup in the report among them) still
 * scrolling behind its dialog. Both are frozen, and each gets its own scrollbar
 * width back as padding so locking does not shift the layout sideways.
 */
const MODAL_SELECTOR = '[aria-modal="true"]';
const INNER_SCROLLERS = '.admin-content';

function lock(el, gap) {
  if (el.dataset.scrollLocked === 'true') return;
  el.dataset.scrollLocked = 'true';
  el.dataset.scrollLockPad = el.style.paddingRight || '';
  el.dataset.scrollLockOverflow = el.style.overflow || '';
  el.style.overflow = 'hidden';
  if (gap > 0) el.style.paddingRight = `${gap}px`;
}

function release(el) {
  if (el.dataset.scrollLocked !== 'true') return;
  el.style.overflow = el.dataset.scrollLockOverflow || '';
  el.style.paddingRight = el.dataset.scrollLockPad || '';
  delete el.dataset.scrollLocked;
  delete el.dataset.scrollLockPad;
  delete el.dataset.scrollLockOverflow;
}

export function applyModalScrollLock(doc = document) {
  const { body, documentElement: html } = doc;
  if (!body) return;
  const open = doc.querySelector(MODAL_SELECTOR) !== null;
  const inner = Array.from(doc.querySelectorAll(INNER_SCROLLERS));

  if (open) {
    lock(body, window.innerWidth - html.clientWidth);
    inner.forEach((el) => lock(el, el.offsetWidth - el.clientWidth));
  } else {
    release(body);
    inner.forEach(release);
  }
}

export default function useModalScrollLock() {
  useEffect(() => {
    applyModalScrollLock();
    const observer = new MutationObserver(() => applyModalScrollLock());
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-modal'],
    });
    return () => {
      observer.disconnect();
      // Unmounting with a dialog still up would otherwise strand the lock.
      release(document.body);
      document.querySelectorAll(INNER_SCROLLERS).forEach(release);
    };
  }, []);
}
